import {
  BarChart3,
  Bell,
  Briefcase,
  CalendarClock,
  Check,
  ClipboardCheck,
  FileCheck2,
  Inbox,
  MessagesSquare,
  Network,
  RefreshCw,
  ScanSearch,
  Settings,
  ShieldAlert,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { useEffect } from 'react';
import { AssessmentPage } from './features/assessments/AssessmentPage';
import { BusinessShell } from './features/business/BusinessShell';
import { ConnectionsPage } from './features/business/ConnectionsPage';
import { BusinessProvider, useIsBusiness } from './features/business/context';
import { HomePage } from './features/business/HomePage';
import { InsurancePage } from './features/business/InsurancePage';
import { LockerPage } from './features/business/LockerPage';
import { MissionsPage } from './features/business/MissionsPage';
import { PlanPage } from './features/business/PlanPage';
import { PublicBadgePage } from './features/business/PublicBadgePage';
import { PassportPage } from './features/passports/PassportPage';
import { PublicPassportPage } from './features/passports/PublicPassportPage';
import { PublicIntakePage } from './features/intake/PublicIntakePage';
import { SubmissionsPage } from './features/intake/SubmissionsPage';
import {} from '@cyberscore/contracts';
import { AlertsPage } from './features/alerts/AlertsPage';
import { IntegrationsPage } from './features/integrations/IntegrationsPage';
import { ConflictsPage } from './features/conflicts/ConflictsPage';
import { SuppliersPage } from './features/suppliers/SuppliersPage';
import { useAlerts } from './features/alerts/api';
import { CampaignsPage } from './features/campaigns/CampaignsPage';

import { ReportPage } from './features/business/ReportPage';
import { AssessmentsPage } from './features/assessments/AssessmentsPage';
import { QuestionFlow } from './features/assessments/QuestionFlow';
import { Client360 } from './features/clients/Client360';
import { ClientsPage } from './features/clients/ClientsPage';
import { ChatGallery } from './features/chat/ChatGallery';
import { ChatScreen } from './features/chat/ChatScreen';
import { DocumentsPage } from './features/documents/DocumentsPage';
import { useSeesPortfolio } from './features/portfolio/api';
import { PortfolioPage } from './features/portfolio/PortfolioPage';
import { ScorePage } from './features/portfolio/ScorePage';
import { ProvidersPage } from './features/providers/ProvidersPage';
import { useRescoreAccess } from './features/rescores/api';
import { ReviewPage } from './features/review/ReviewPage';
import { MessagesPage } from './features/messages/MessagesPage';
import { useConversations } from './features/messages/api';
import { NoticesPage } from './features/messages/NoticesPage';
import { ClientFieldsPage } from './features/clients/ClientFieldsPage';
import { InsurerFormsPage } from './features/insurers/InsurerFormsPage';
import { InsurersPage } from './features/insurers/InsurersPage';
import { RescoresPage } from './features/rescores/RescoresPage';
import { useEvidenceAccess } from './features/evidence/api';
import { EvidencePage } from './features/evidence/EvidencePage';
import { usePeopleAccess } from './features/people/api';
import { OrganisationsPage, useOrganisationsAccess } from './features/people/OrganisationsPage';
import { PeoplePage } from './features/people/PeoplePage';
import { RolesPage } from './features/people/RolesPage';
import { useStudioAccess } from './features/studio/api';
import { ItemsPage } from './features/studio/ItemsPage';
import { JourneyBuilder } from './features/studio/JourneyBuilder';
import { QuestionEditor } from './features/studio/QuestionEditor';
import { ReleasesPage } from './features/studio/ReleasesPage';
import { InsurerFormEditor } from './features/studio/InsurerFormEditor';
import { ProfileEditor } from './features/studio/ProfileEditor';
import { RuleEditor } from './features/studio/RuleEditor';
import { StudioPage } from './features/studio/StudioPage';
import { brandStyle } from './lib/brand';
import { href, navigate, parseRoute, useHash, type Route } from './lib/router';
import { useSession, can } from './lib/session';
import { AccountMenu } from './ui/AccountMenu';
import { AccountPage, MfaGate } from './features/auth/AccountPage';
import { BrandingPage } from './features/branding/BrandingPage';
import { useSettingsAccess } from './features/tenant/access';
import { Tenant360 } from './features/tenant/Tenant360';
import { ForgotPage, ResetPage, SignInPage, SignUpPage, VerifyPage } from './features/auth/AuthPages';
import { NoOrganisation } from './features/auth/NoOrganisation';

interface NavPage {
  label: string;
  icon: LucideIcon;
  to: string;
  on: Route['name'][];
  /** A count beside the label (unread alerts). */
  count?: number;
}

/**
 * The staff navigation (task 102, decision 0073): five groups in the sidebar, Clients, Portfolio, Review,
 * Assessments and Settings, each opening its first page; a group with several pages shows them as tabs
 * across the top. A group or page the person may not open is left out.
 */
interface NavGroup {
  label: string;
  icon: LucideIcon;
  pages: NavPage[];
}

const STUDIO_ROUTES: Route['name'][] = [
  'studio',
  'studio-journey',
  'studio-questions',
  'studio-question',
  'studio-rules',
  'studio-rule',
  'studio-profile',
  'studio-insurer-form',
  'releases',
];

/** Signing in (decision 0033): only while signed out. */
const AUTH_ROUTES: Route['name'][] = ['sign-in', 'sign-up', 'forgot', 'verify', 'reset'];

/** The business owner's own pages (the prototype's CyberCheck). */
const BUSINESS_ROUTES: Route['name'][] = [
  'home',
  'missions',
  'connections',
  'locker',
  'insurance',
  'plan',
  'report',
  'passport',
];

export function App() {
  const { me, tenant, loading, switchTenant, signOut } = useSession();
  const hash = useHash();
  const platform = tenant?.kind === 'platform';
  const seesPortfolio = useSeesPortfolio();
  const studio = useStudioAccess().sees;
  const rescores = useRescoreAccess().sees;
  const evidence = useEvidenceAccess();
  const people = usePeopleAccess();
  // Roles and what each may do (task 78).
  const roleViewer = can(tenant, 'roles.view');
  const organisations = useOrganisationsAccess();
  const business = useIsBusiness();
  // Tenant 360 (task 102): anyone who may open one of the organisation's settings.
  const settings = useSettingsAccess();
  // The organisation's colours (task 97): its administrators; a business shows the platform's.
  const branding = !business && can(tenant, 'organisation.edit');
  // Submissions through the insurer's or broker's links (task 68).
  const intake = !platform && !business && can(tenant, 'intake.view');
  // Renewal campaigns (task 69).
  const campaigns = !platform && !business && can(tenant, 'campaigns.view');
  // Alerts (task 70): everyone an alert rule can tell.
  const alerts = !platform && !business && can(tenant, 'alerts.view');
  const unreadAlerts = useAlerts(alerts).data?.unread ?? 0;
  // Messages (task 65): everyone who takes part in assessments' conversations, with what is unread.
  const messages = !platform && can(tenant, 'messages.view');
  const unreadMessages = (useConversations(messages && !business).data ?? []).reduce(
    (n, c) => n + c.unread,
    0,
  );
  // API keys and webhooks (task 71): insurers and brokers.
  const integrations = !platform && !business && can(tenant, 'api_keys.view', 'webhooks.view');
  // Answers the internet check contradicts (task 72): underwriters, admins and auditors.
  const conflicts = !platform && !business && can(tenant, 'conflicts.view');
  const suppliers = !platform && !business && can(tenant, 'suppliers.view');
  // Insurers' proposal forms and placing the client with each (tasks 88–93): insurer and broker staff.
  const insurers = !platform && !business && can(tenant, 'assessments.view');
  const insurerForms = !platform && !business && can(tenant, 'intake.view');
  // Insurer staff land on their clients; the businesses answering on their home, everyone else on their
  // assessments.
  const home = platform
    ? href.tenant()
    : seesPortfolio
      ? href.clients()
      : business
        ? href.home()
        : href.assessments();
  const parsed = parseRoute(hash);
  // Platform staff configure the product; everyone else works on assessments. Each lands on their own home.
  // The chat design gallery is open to everyone signed in; the studio to everyone with a configuration role.
  const allowed = (r: Route) => {
    if (
      r.name === 'chat-design' ||
      r.name === 'badge' ||
      r.name === 'pass' ||
      r.name === 'apply' ||
      r.name === 'account'
    )
      return true;
    if (AUTH_ROUTES.includes(r.name)) return false;
    if (BUSINESS_ROUTES.includes(r.name)) return business;
    if (STUDIO_ROUTES.includes(r.name)) return studio;
    if (r.name === 'rescores') return rescores;
    if (r.name === 'evidence') return evidence;
    if (r.name === 'people') return people;
    if (r.name === 'roles') return roleViewer;
    if (r.name === 'branding') return branding;
    if (r.name === 'tenant') return settings.any;
    if (r.name === 'organisations') return organisations;
    if (r.name === 'submissions') return intake;
    if (r.name === 'campaigns') return campaigns;
    if (r.name === 'alerts') return alerts;
    if (r.name === 'integrations') return integrations;
    if (r.name === 'conflicts') return conflicts;
    if (r.name === 'suppliers') return suppliers;
    if (r.name === 'insurers') return insurers;
    if (r.name === 'insurer-forms') return insurerForms;
    return platform
      ? r.name === 'providers'
      : r.name !== 'providers' &&
          r.name !== 'unknown' &&
          ((r.name !== 'portfolio' && r.name !== 'clients' && r.name !== 'client') || seesPortfolio);
  };
  const route = allowed(parsed) ? parsed : null;

  // Only while the address is still the one this render saw: a link opened since (right after signing in,
  // say) is the next render's to judge, not something to replace with home.
  useEffect(() => {
    if (me && tenant && !route && window.location.hash === hash) navigate(home, { replace: true });
  }, [me, tenant, route, home, hash]);

  // A public badge needs no sign-in, and looks the same to everyone.
  if (parsed.name === 'badge') return <PublicBadgePage token={parsed.token} />;
  // So does a cyber passport (task 76).
  if (parsed.name === 'pass') return <PublicPassportPage token={parsed.token} />;
  // So does a submission link's form (task 68).
  if (parsed.name === 'apply') return <PublicIntakePage token={parsed.token} />;
  // Links from emails work signed in or not: they sign in as their account.
  if (parsed.name === 'verify') return <VerifyPage token={parsed.token} />;
  if (parsed.name === 'reset') return <ResetPage token={parsed.token} />;
  if (loading) return null;
  if (!me)
    return parsed.name === 'sign-up' ? (
      <SignUpPage />
    ) : parsed.name === 'forgot' ? (
      <ForgotPage />
    ) : (
      <SignInPage />
    );
  if (me.mfa_required) return <MfaGate />;
  if (!tenant)
    return parsed.name === 'account' ? (
      <div className="biz gate">
        <AccountPage />
      </div>
    ) : (
      <NoOrganisation />
    );
  if (business)
    return (
      <BusinessProvider key={tenant?.id}>
        <BusinessShell route={route}>
          <Screen route={route} />
        </BusinessShell>
      </BusinessProvider>
    );

  const page = (on: boolean, p: NavPage) => (on ? [p] : []);
  const groups: NavGroup[] = (
    platform
      ? []
      : [
          {
            label: 'Clients',
            icon: Briefcase,
            pages: [
              ...page(seesPortfolio, { label: 'Clients', icon: Briefcase, to: href.clients(), on: ['clients', 'client', 'review', 'insurers'] }), // prettier-ignore
              ...page(intake, {
                label: 'Submissions',
                icon: Inbox,
                to: href.submissions(),
                on: ['submissions'],
              }),
              ...page(campaigns, {
                label: 'Renewals',
                icon: CalendarClock,
                to: href.campaigns(),
                on: ['campaigns'],
              }),
            ],
          },
          {
            label: 'Portfolio',
            icon: BarChart3,
            pages: [
              ...page(seesPortfolio, { label: 'Portfolio', icon: BarChart3, to: href.portfolio(), on: ['portfolio', 'score'] }), // prettier-ignore
              ...page(alerts, {
                label: 'Alerts',
                icon: Bell,
                to: href.alerts(),
                on: ['alerts'],
                count: unreadAlerts,
              }),
              ...page(suppliers, {
                label: 'Suppliers',
                icon: Network,
                to: href.suppliers(),
                on: ['suppliers'],
              }),
            ],
          },
          {
            label: 'Review',
            icon: ScanSearch,
            pages: [
              ...page(conflicts, {
                label: 'Conflicts',
                icon: ShieldAlert,
                to: href.conflicts(),
                on: ['conflicts'],
              }),
              ...page(rescores, {
                label: 'Re-scores',
                icon: RefreshCw,
                to: href.rescores(),
                on: ['rescores'],
              }),
              ...page(evidence, {
                label: 'Evidence pack',
                icon: FileCheck2,
                to: href.evidence(),
                on: ['evidence'],
              }),
            ],
          },
          {
            label: 'Assessments',
            icon: ClipboardCheck,
            pages: [
              {
                label: 'Assessments',
                icon: ClipboardCheck,
                to: href.assessments(),
                on: ['assessments', 'assessment', 'answer', 'chat', 'documents', ...(seesPortfolio ? [] : ['score' as const])] as Route['name'][], // prettier-ignore
              },
            ],
          },
          {
            label: 'Messages',
            icon: MessagesSquare,
            pages: page(messages, {
              label: 'Messages',
              icon: MessagesSquare,
              to: href.messages(null),
              on: ['messages'],
              count: unreadMessages,
            }),
          },
        ]
  ).concat({
    label: 'Settings',
    icon: Settings,
    // Every settings page is a tab of Tenant 360; the pages' own addresses still open them on their own.
    pages: page(settings.any, {
      label: 'Settings',
      icon: Settings,
      to: href.tenant(),
      on: ['tenant', 'people', 'roles', 'branding', 'notices', 'client-fields', 'insurer-forms', 'integrations', 'providers', 'organisations', ...STUDIO_ROUTES] as Route['name'][], // prettier-ignore
    }),
  });
  // Design reviews happen on local and demo builds; production keeps the route but not the link.
  if (import.meta.env.DEV)
    groups.push({
      label: 'Chat design',
      icon: MessagesSquare,
      pages: [{ label: 'Chat design', icon: MessagesSquare, to: href.chatDesign(), on: ['chat-design'] }],
    });
  const nav = groups.filter((g) => g.pages.length);
  const isOn = (p: NavPage) => !!route && p.on.includes(route.name);
  const open = nav.find((g) => g.pages.some(isOn));

  return (
    <div className="shell" style={brandStyle(tenant)}>
      <aside className="side">
        <div className="brand">
          <span className="brand-mark">
            <ShieldCheck size={17} />
          </span>
          CyberScore
        </div>
        <nav aria-label="Main" className="side-nav">
          {nav.map((g) => {
            const count = g.pages.reduce((n, p) => n + (p.count ?? 0), 0);
            return (
              <a
                key={g.label}
                className="nav-item"
                href={g.pages[0]!.to}
                aria-current={g === open ? 'page' : undefined}
              >
                <g.icon size={17} /> {g.label}
                {count > 0 && (
                  <span className="nav-count" aria-label={`${count} unread`}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </a>
            );
          })}
        </nav>
      </aside>
      <main className="main">
        <header className="staff-top">
          {open && open.pages.length > 1 ? (
            <nav className="subnav" aria-label={`${open.label} pages`}>
              {open.pages.map((p) => (
                <a key={p.to} href={p.to} aria-current={isOn(p) ? 'page' : undefined}>
                  <p.icon size={15} /> {p.label}
                  {!!p.count && <span className="nav-count">{p.count > 99 ? '99+' : p.count}</span>}
                </a>
              ))}
            </nav>
          ) : (
            <span className="grow" />
          )}
          <AccountMenu
            name={me.user.name ?? me.user.email ?? '?'}
            org={tenant?.name ?? null}
            onSignOut={signOut}
          >
            {me.tenants.length > 1 && (
              <>
                <div className="menu-label">Organisation</div>
                {me.tenants.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={t.id === tenant?.id}
                    className="menu-item"
                    onClick={() => switchTenant(t.id)}
                  >
                    <span className="check">{t.id === tenant?.id && <Check size={14} />}</span>
                    {t.name}
                  </button>
                ))}
                <div className="menu-sep" />
              </>
            )}
          </AccountMenu>
        </header>
        <Screen route={route} />
      </main>
    </div>
  );
}

function Screen({ route }: { route: Route | null }) {
  switch (route?.name) {
    case 'client':
      return <Client360 key={route.key} customerKey={route.key} />;
    case 'clients':
      return <ClientsPage />;
    case 'assessments':
      return <AssessmentsPage />;
    case 'assessment':
      return <AssessmentPage key={route.id} id={route.id} />;
    case 'answer':
      return <QuestionFlow key={route.id} id={route.id} question={route.question} />;
    case 'chat':
      return <ChatScreen key={route.id} id={route.id} />;
    case 'documents':
      return <DocumentsPage key={route.id} id={route.id} />;
    case 'portfolio':
      return <PortfolioPage />;
    case 'rescores':
      return <RescoresPage />;
    case 'evidence':
      return <EvidencePage />;
    case 'people':
      return <PeoplePage />;
    case 'branding':
      return <BrandingPage />;
    case 'tenant':
      return <Tenant360 />;
    case 'roles':
      return <RolesPage />;
    case 'organisations':
      return <OrganisationsPage />;
    case 'score':
      return <ScorePage key={route.id} id={route.id} />;
    case 'review':
      return <ReviewPage key={route.id} id={route.id} />;
    case 'messages':
      // One page for every conversation, so the list beside them stays put while another one opens.
      return <MessagesPage id={route.id} />;
    case 'insurers':
      return <InsurersPage key={route.id} id={route.id} />;
    case 'insurer-forms':
      return <InsurerFormsPage />;
    case 'notices':
      return <NoticesPage />;
    case 'client-fields':
      return <ClientFieldsPage />;
    case 'submissions':
      return <SubmissionsPage />;
    case 'campaigns':
      return <CampaignsPage />;
    case 'alerts':
      return <AlertsPage />;
    case 'integrations':
      return <IntegrationsPage />;
    case 'conflicts':
      return <ConflictsPage />;
    case 'suppliers':
      return <SuppliersPage />;
    case 'studio':
      return <StudioPage />;
    case 'studio-journey':
      return <JourneyBuilder key={route.id} id={route.id} />;
    case 'studio-questions':
      return <ItemsPage key="question" kind="question" />;
    case 'studio-question':
      return <QuestionEditor key={route.id} id={route.id} />;
    case 'studio-rules':
      return <ItemsPage key="rule" kind="rule" />;
    case 'studio-rule':
      return <RuleEditor key={route.id} id={route.id} />;
    case 'studio-profile':
      return <ProfileEditor key={route.id} id={route.id} />;
    case 'studio-insurer-form':
      return <InsurerFormEditor key={route.id} id={route.id} />;
    case 'releases':
      return <ReleasesPage />;
    case 'providers':
      return <ProvidersPage />;
    case 'chat-design':
      return <ChatGallery />;
    case 'home':
      return <HomePage />;
    case 'missions':
      return <MissionsPage mission={route.mission} />;
    case 'connections':
      return <ConnectionsPage />;
    case 'locker':
      return <LockerPage />;
    case 'insurance':
      return <InsurancePage />;
    case 'plan':
      return <PlanPage />;
    case 'report':
      return <ReportPage />;
    case 'passport':
      return <PassportPage />;
    case 'account':
      return <AccountPage />;
    default:
      return null;
  }
}
